import { createContext, useContext, type ComponentType, type ReactNode } from 'react';

/**
 * Shape of the read-only document viewer component used by hover/preview
 * surfaces (e.g. components/link, components/entity-preview-card). The
 * concrete renderer lives in the editor feature (DocumentViewer wraps the
 * heavy Lexical reader); registering it via context here lets shared
 * components consume document previews without violating the
 * components -> features layer boundary.
 *
 * Mirror of canvas-preview-context for documents — see app/router.tsx for
 * the production wiring.
 */
export type DocumentViewerRenderer = ComponentType<{
  entityId: string;
  onStats?: (stats: { wordCount: number }) => void;
}>;

const DocumentViewerContext = createContext<DocumentViewerRenderer | null>(null);

interface DocumentViewerProviderProps {
  renderer: DocumentViewerRenderer;
  children: ReactNode;
}

export function DocumentViewerProvider({ renderer, children }: DocumentViewerProviderProps) {
  return (
    <DocumentViewerContext.Provider value={renderer}>
      {children}
    </DocumentViewerContext.Provider>
  );
}

/**
 * Returns the registered document viewer renderer, or `null` when no
 * provider is mounted. Callers should fall back to a skeleton when the
 * renderer is unavailable.
 */
export function useDocumentViewerRenderer(): DocumentViewerRenderer | null {
  return useContext(DocumentViewerContext);
}
